import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import Webcam from 'react-webcam';
import jsQR from 'jsqr';
import { API_BASE_URL } from '../config';

const API = `${API_BASE_URL}/api`;

function QRScanner() {
  const [scanType, setScanType] = useState('clipcard'); // 'clipcard', 'giftcard', or 'booking'
  const [code, setCode] = useState('');
  const [clipcard, setClipcard] = useState(null);
  const [giftcard, setGiftcard] = useState(null);
  const [booking, setBooking] = useState(null);
  const [bookingStarted, setBookingStarted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [redeeming, setRedeeming] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const webcamRef = useRef(null);
  const scanIntervalRef = useRef(null);
  const canvasRef = useRef(null);
  const lastRedeemTimeRef = useRef(null);

  const lookupClipcard = async () => {
    await lookupByCode(code, 'clipcard');
  };

  const lookupGiftcard = async () => {
    await lookupByCode(code, 'giftcard');
  };

  const redeemClip = async (force = false) => {
    if (!clipcard) return;
    
    // Check for double-click within 60 seconds (1 minute)
    const now = Date.now();
    const lastRedeemTime = lastRedeemTimeRef.current;
    const timeSinceLastRedeem = lastRedeemTime ? (now - lastRedeemTime) : null;
    
    console.log('Redeem click:', { 
      force, 
      lastRedeemTime, 
      timeSinceLastRedeem,
      shouldWarn: timeSinceLastRedeem && timeSinceLastRedeem < 60000
    });
    
    if (!force && lastRedeemTime && timeSinceLastRedeem < 60000) {
      console.log('Showing confirmation dialog');
      setShowConfirmDialog(true);
      return;
    }

    console.log('Processing redemption...');
    
    // Set the time IMMEDIATELY using ref (synchronous, not async state)
    lastRedeemTimeRef.current = now;
    setRedeeming(true);
    
    try {
      const response = await axios.post(`${API}/clipcards/${clipcard.code}/redeem`, {
        redeemed_by: 'Staff',
        location: 'Wash Station',
        plate: clipcard.related_plate || 'KLIPPIKORT'
      });

      if (response.data.success) {
        toast.success(response.data.message);
        
        if (response.data.job_created) {
          toast.info('✅ Verk búið til - sýnilegt í Verk lista');
        }
        
        // Update local clipcard data WITHOUT resetting lastRedeemTime
        setClipcard(prev => ({
          ...prev,
          remaining_clips: response.data.remaining_clips,
          status: response.data.status
        }));
        
        setShowConfirmDialog(false);
      }
    } catch (error) {
      console.error('Failed to redeem clip:', error);
      toast.error(error.response?.data?.detail || 'Villa við að nota klipp');
    } finally {
      setRedeeming(false);
    }
  };

  const resetScanner = () => {
    setCode('');
    setClipcard(null);
    setGiftcard(null);
    setBooking(null);
    setBookingStarted(false);
    lastRedeemTimeRef.current = null;
    stopScanning();
  };

  // Start live QR scanning
  const startScanning = () => {
    setScanning(true);
    setClipcard(null);
    setGiftcard(null);
    setBooking(null);
    setBookingStarted(false);
    setCode('');
  };

  // Stop scanning
  const stopScanning = () => {
    setScanning(false);
    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current);
      scanIntervalRef.current = null;
    }
  };

  // Scan QR code from video stream
  useEffect(() => {
    if (!scanning || !webcamRef.current) return;

    const scanQR = () => {
      try {
        const video = webcamRef.current?.video;
        if (!video || video.readyState !== video.HAVE_ENOUGH_DATA) {
          return;
        }

        const canvas = canvasRef.current;
        if (!canvas) return;

        const context = canvas.getContext('2d');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        context.drawImage(video, 0, 0, canvas.width, canvas.height);
        const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
        
        const qrCode = jsQR(imageData.data, imageData.width, imageData.height, {
          inversionAttempts: "dontInvert",
        });

        if (qrCode) {
          stopScanning();
          toast.success('QR kóði fundinn!');
          
          // Check if this is a booking QR code (URL format)
          const qrData = qrCode.data;
          if (qrData.includes('/api/public/qr/start/')) {
            // Extract job ID from URL
            const jobId = qrData.split('/api/public/qr/start/')[1];
            setCode(jobId);
            setScanType('booking');
            lookupByCode(jobId, 'booking');
          } else {
            setCode(qrData);
            lookupByCode(qrData, scanType);
          }
        }
      } catch (error) {
        console.error('Scan error:', error);
      }
    };

    // Scan every 200ms for better detection
    scanIntervalRef.current = setInterval(scanQR, 200);

    return () => {
      if (scanIntervalRef.current) {
        clearInterval(scanIntervalRef.current);
      }
    };
  }, [scanning, scanType]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopScanning();
    };
  }, []);

  const lookupByCode = async (codeToLookup, type) => {
    const searchCode = codeToLookup || code;
    const searchType = type || scanType;
    
    if (!searchCode) {
      toast.error('Vinsamlegast sláðu inn kóða');
      return;
    }

    setLoading(true);
    try {
      if (searchType === 'clipcard') {
        const response = await axios.get(`${API}/clipcards/${searchCode}`);
        setClipcard(response.data);
        setGiftcard(null);
        setBooking(null);
        toast.success('Klippikort fannst!');
      } else if (searchType === 'giftcard') {
        const response = await axios.get(`${API}/giftcards/${searchCode}`);
        setGiftcard(response.data);
        setClipcard(null);
        setBooking(null);
        toast.success('Gjafabréf fannst!');
      } else if (searchType === 'booking') {
        // For bookings, we look up the job first to display info
        const token = localStorage.getItem('access_token');
        const response = await axios.get(`${API}/jobs/${searchCode}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setBooking(response.data);
        setClipcard(null);
        setGiftcard(null);
        setBookingStarted(false);
        toast.success('Bókun fannst!');
      }
    } catch (error) {
      console.error(`Failed to lookup ${searchType}:`, error);
      if (error.response?.status === 404) {
        if (searchType === 'clipcard') {
          toast.error('Klippikort fannst ekki með þessum kóða');
        } else if (searchType === 'giftcard') {
          toast.error('Gjafabréf fannst ekki með þessum kóða');
        } else {
          toast.error('Bókun fannst ekki með þessum kóða');
        }
      } else {
        toast.error(error.response?.data?.detail || `Villa við að leita`);
      }
      setClipcard(null);
      setGiftcard(null);
      setBooking(null);
    } finally {
      setLoading(false);
    }
  };

  // Start booking job via QR
  const startBookingJob = async () => {
    if (!booking) return;
    
    setRedeeming(true);
    try {
      // Call the public QR start endpoint
      const response = await fetch(`${API}/public/qr/start/${booking.id}`);
      
      if (response.ok) {
        setBookingStarted(true);
        setBooking(prev => ({
          ...prev,
          status: 'in_progress',
          started_at: new Date().toISOString()
        }));
        toast.success('✅ Verk byrjað!');
      } else {
        const html = await response.text();
        if (html.includes('þegar í gangi') || html.includes('in_progress')) {
          toast.info('Verk er nú þegar í gangi');
          setBookingStarted(true);
        } else if (html.includes('lokið') || html.includes('done')) {
          toast.info('Þessu verki er þegar lokið');
        } else {
          toast.error('Villa við að byrja verk');
        }
      }
    } catch (error) {
      console.error('Failed to start booking job:', error);
      toast.error('Villa við að byrja verk');
    } finally {
      setRedeeming(false);
    }
  };

  const lookupBooking = async () => {
    await lookupByCode(code, 'booking');
  };

  const copyCodeToClipboard = () => {
    const codeToCopy = giftcard?.code;
    if (codeToCopy) {
      navigator.clipboard.writeText(codeToCopy);
      toast.success('Kóði afritaður! Þú getur notað hann í greiðsluferli');
    }
  };

  const formatExpiration = (expirationDate) => {
    if (!expirationDate) return 'Ótakmarkað';
    const date = new Date(expirationDate);
    return date.toLocaleDateString('is-IS');
  };


  return (
    <div className="max-w-2xl mx-auto p-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <span className="text-3xl">📱</span>
            QR Kóða Skanni
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Tabs for Clipcard / Giftcard */}
          <Tabs value={scanType} onValueChange={(value) => {
            setScanType(value);
            resetScanner();
          }}>
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="clipcard">🎫 Klippikort</TabsTrigger>
              <TabsTrigger value="giftcard">🎁 Gjafabréf</TabsTrigger>
              <TabsTrigger value="booking">🚗 Bókanir</TabsTrigger>
            </TabsList>

            <TabsContent value="clipcard" className="space-y-4">
              {/* Code Input for Clipcard */}
              <div className="space-y-2">
                <Label>Sláðu inn klippikort kóða</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="CC-XXXXXXXX"
                    value={code}
                    onChange={(e) => setCode(e.target.value.toUpperCase())}
                    onKeyDown={(e) => e.key === 'Enter' && lookupClipcard()}
                    disabled={!!clipcard}
                  />
                  {!clipcard ? (
                    <Button 
                      onClick={lookupClipcard} 
                      disabled={loading || !code}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      {loading ? '⌛ Leita...' : '🔍 Leita'}
                    </Button>
                  ) : (
                    <Button 
                      onClick={resetScanner}
                      variant="outline"
                    >
                      ✕ Hreinsa
                    </Button>
                  )}
                </div>
                <p className="text-xs text-slate-500">
                  Sláðu inn kóðann sem byrjar á "CC-" eða skannaðu QR kóðann
                </p>
              </div>

              {/* Live Camera Scanner */}
              {!clipcard && !scanning && (
                <div className="space-y-2">
                  <Button
                    onClick={startScanning}
                    className="bg-green-600 hover:bg-green-700 w-full text-lg py-6"
                    size="lg"
                  >
                    📷 Byrja að skanna QR kóða
                  </Button>
                </div>
              )}

              {/* Camera View with Hidden Canvas */}
              {scanning && (
                <div className="space-y-2">
                  <div className="relative border-4 border-green-500 rounded-lg overflow-hidden bg-black">
                    <Webcam
                      ref={webcamRef}
                      audio={false}
                      screenshotFormat="image/jpeg"
                      videoConstraints={{
                        facingMode: { ideal: 'environment' },
                        width: { ideal: 1920 },
                        height: { ideal: 1080 }
                      }}
                      className="w-full"
                      onUserMediaError={(error) => {
                        console.error('Camera error:', error);
                        toast.error('Villa við að opna myndavél');
                        stopScanning();
                      }}
                    />
                    
                    {/* Scanning overlay */}
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <div className="w-64 h-64 border-4 border-green-400 rounded-lg shadow-lg animate-pulse">
                        <div className="absolute top-0 left-0 w-8 h-8 border-t-4 border-l-4 border-green-400"></div>
                        <div className="absolute top-0 right-0 w-8 h-8 border-t-4 border-r-4 border-green-400"></div>
                        <div className="absolute bottom-0 left-0 w-8 h-8 border-b-4 border-l-4 border-green-400"></div>
                        <div className="absolute bottom-0 right-0 w-8 h-8 border-b-4 border-r-4 border-green-400"></div>
                      </div>
                    </div>
                    
                    {/* Scanning text */}
                    <div className="absolute bottom-4 left-0 right-0 text-center">
                      <p className="text-white font-bold bg-black bg-opacity-70 px-4 py-2 rounded inline-block">
                        🔍 Að skanna QR kóða...
                      </p>
                    </div>
                  </div>
                  
                  {/* Hidden canvas for processing */}
                  <canvas ref={canvasRef} style={{ display: 'none' }} />
                  
                  <Button
                    onClick={stopScanning}
                    variant="outline"
                    className="w-full"
                  >
                    ✕ Hætta við
                  </Button>
                </div>
              )}

              {/* Clipcard Display */}
              {clipcard && (
                <div className="border-2 border-green-500 rounded-lg p-6 bg-green-50">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-bold text-green-900">
                        {clipcard.customer?.name || clipcard.customer_name}
                      </h3>
                      <p className="text-sm text-green-700">
                        Kennitala: {clipcard.customer?.kennitala || 'N/A'}
                      </p>
                    </div>
                    <Badge variant={
                      clipcard.status === 'active' ? 'default' : 
                      clipcard.status === 'used' ? 'secondary' : 
                      'destructive'
                    }>
                      {clipcard.status === 'active' ? 'VIRKT' : 
                       clipcard.status === 'used' ? 'UPPURIÐ' : 
                       'ÚTRUNNIÐ'}
                    </Badge>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="bg-white p-3 rounded">
                      <p className="text-xs text-slate-600">Kóði</p>
                      <p className="font-mono font-bold">{clipcard.code}</p>
                    </div>
                    <div className="bg-white p-3 rounded">
                      <p className="text-xs text-slate-600">Bílastærð</p>
                      <p className="font-bold">{clipcard.vehicle_size}</p>
                    </div>
                    <div className="bg-white p-3 rounded">
                      <p className="text-xs text-slate-600">Þvottar eftir</p>
                      <p className={`text-2xl font-bold ${
                        clipcard.remaining_clips <= 2 ? 'text-red-600' : 'text-green-600'
                      }`}>
                        {clipcard.remaining_clips} / {clipcard.total_clips}
                      </p>
                    </div>
                    <div className="bg-white p-3 rounded">
                      <p className="text-xs text-slate-600">Gildir til</p>
                      <p className="font-bold">
                        {new Date(clipcard.expiration_date).toLocaleDateString('is-IS')}
                      </p>
                    </div>
                  </div>

                  {clipcard.status === 'active' && clipcard.remaining_clips > 0 ? (
                    <Button
                      onClick={() => redeemClip()}
                      disabled={redeeming}
                      className="w-full bg-green-600 hover:bg-green-700 text-lg py-6"
                      size="lg"
                    >
                      {redeeming ? '⌛ Vinnsla...' : '✂️ Nota einn klipp'}
                    </Button>
                  ) : (
                    <div className="bg-red-100 border-2 border-red-500 p-4 rounded text-center">
                      <p className="font-bold text-red-900">
                        {clipcard.status === 'expired' 
                          ? '⚠️ Klippikort er útrunnið'
                          : '⚠️ Engir klippar eftir'}
                      </p>
                    </div>
                  )}

                  {/* Usage History */}
                  {clipcard.usage_history && clipcard.usage_history.length > 0 && (
                    <details className="mt-4 text-sm">
                      <summary className="cursor-pointer font-medium text-green-900 hover:text-green-700">
                        📋 Síðustu notkun ({clipcard.usage_history.length})
                      </summary>
                      <div className="mt-2 space-y-1 pl-4 text-slate-600">
                        {clipcard.usage_history.slice(-3).reverse().map((usage, idx) => (
                          <div key={idx}>
                            • {new Date(usage.date).toLocaleString('is-IS')} - 
                            Notað af {usage.redeemed_by}
                            {usage.location && ` (${usage.location})`}
                          </div>
                        ))}
                      </div>
                    </details>
                  )}
                </div>
              )}

              {/* Instructions */}
              {!clipcard && !scanning && (
                <div className="bg-blue-50 p-4 rounded-lg text-sm space-y-2">
                  <p className="font-medium text-blue-900">📖 Leiðbeiningar:</p>
                  <ul className="text-blue-700 space-y-1 list-disc list-inside">
                    <li>Sláðu inn klippikort kóðann handvirkt</li>
                    <li>Eða smelltu á "Byrja að skanna"</li>
                    <li>Beindu myndavélinni að QR kóðanum</li>
                    <li>Haltu símanum kyrrum í 1-2 sekúndur</li>
                    <li>Kerfið finnur QR kóðann sjálfkrafa</li>
                  </ul>
                </div>
              )}
            </TabsContent>

            {/* GIFTCARD TAB */}
            <TabsContent value="giftcard" className="space-y-4">
              {/* Code Input for Giftcard */}
              <div className="space-y-2">
                <Label>Sláðu inn gjafabréfskóða</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="GC-XXXXXXXX"
                    value={code}
                    onChange={(e) => setCode(e.target.value.toUpperCase())}
                    onKeyDown={(e) => e.key === 'Enter' && lookupGiftcard()}
                    disabled={!!giftcard}
                  />
                  {!giftcard ? (
                    <Button 
                      onClick={lookupGiftcard} 
                      disabled={loading || !code}
                      className="bg-purple-600 hover:bg-purple-700"
                    >
                      {loading ? '⌛ Leita...' : '🔍 Leita'}
                    </Button>
                  ) : (
                    <Button 
                      onClick={resetScanner}
                      variant="outline"
                    >
                      ✕ Hreinsa
                    </Button>
                  )}
                </div>
                <p className="text-xs text-slate-500">
                  Sláðu inn kóðann sem byrjar á "GC-" eða skannaðu QR kóðann
                </p>
              </div>

              {/* Live Camera Scanner for Giftcard */}
              {!giftcard && !scanning && (
                <div className="space-y-2">
                  <Button
                    onClick={startScanning}
                    className="bg-green-600 hover:bg-green-700 w-full text-lg py-6"
                    size="lg"
                  >
                    📷 Byrja að skanna QR kóða
                  </Button>
                </div>
              )}

              {/* Camera View (shared with clipcard) */}
              {scanning && (
                <div className="space-y-2">
                  <div className="relative border-4 border-green-500 rounded-lg overflow-hidden bg-black">
                    <Webcam
                      ref={webcamRef}
                      audio={false}
                      screenshotFormat="image/jpeg"
                      videoConstraints={{
                        facingMode: { ideal: 'environment' },
                        width: { ideal: 1920 },
                        height: { ideal: 1080 }
                      }}
                      className="w-full"
                      onUserMediaError={(error) => {
                        console.error('Camera error:', error);
                        toast.error('Villa við að opna myndavél');
                        stopScanning();
                      }}
                    />
                    
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <div className="w-64 h-64 border-4 border-green-400 rounded-lg shadow-lg animate-pulse">
                        <div className="absolute top-0 left-0 w-8 h-8 border-t-4 border-l-4 border-green-400"></div>
                        <div className="absolute top-0 right-0 w-8 h-8 border-t-4 border-r-4 border-green-400"></div>
                        <div className="absolute bottom-0 left-0 w-8 h-8 border-b-4 border-l-4 border-green-400"></div>
                        <div className="absolute bottom-0 right-0 w-8 h-8 border-b-4 border-r-4 border-green-400"></div>
                      </div>
                    </div>
                    
                    <div className="absolute bottom-4 left-0 right-0 text-center">
                      <p className="text-white font-bold bg-black bg-opacity-70 px-4 py-2 rounded inline-block">
                        🔍 Að skanna QR kóða...
                      </p>
                    </div>
                  </div>
                  
                  <canvas ref={canvasRef} style={{ display: 'none' }} />
                  
                  <Button
                    onClick={stopScanning}
                    variant="outline"
                    className="w-full"
                  >
                    ✕ Hætta við
                  </Button>
                </div>
              )}

              {/* Giftcard Display */}
              {giftcard && (
                <div className={`border-2 rounded-lg p-6 ${
                  giftcard.giftcard_type === 'service' 
                    ? 'border-green-500 bg-green-50' 
                    : 'border-purple-500 bg-purple-50'
                }`}>
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className={`text-lg font-bold ${
                        giftcard.giftcard_type === 'service' ? 'text-green-900' : 'text-purple-900'
                      }`}>
                        {giftcard.recipient_name || (giftcard.giftcard_type === 'service' ? 'Þjónustugjafabréf' : 'Gjafabréf')}
                      </h3>
                      <p className={`text-sm ${giftcard.giftcard_type === 'service' ? 'text-green-700' : 'text-purple-700'}`}>
                        {giftcard.giftcard_type === 'service' 
                          ? `🛠️ ${giftcard.service_name || 'Þjónusta'}` 
                          : `Format: ${giftcard.format === 'A4' ? '📄 A4 (einskiptis)' : '💳 Kort (fjölnota)'}`
                        }
                      </p>
                    </div>
                    <Badge variant={
                      giftcard.giftcard_type === 'service'
                        ? (giftcard.service_redeemed ? 'secondary' : 'default')
                        : (giftcard.balance > 0 ? 'default' : 'secondary')
                    } className={
                      giftcard.giftcard_type === 'service'
                        ? (giftcard.service_redeemed ? 'bg-gray-500' : 'bg-green-600')
                        : ''
                    }>
                      {giftcard.giftcard_type === 'service'
                        ? (giftcard.service_redeemed ? 'NOTAÐ' : 'VIRKT')
                        : (giftcard.balance > 0 ? 'VIRKT' : 'NOTAÐ')
                      }
                    </Badge>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="bg-white p-3 rounded">
                      <p className="text-xs text-slate-600">Kóði</p>
                      <p className="font-mono font-bold text-sm">{giftcard.code}</p>
                    </div>
                    <div className="bg-white p-3 rounded">
                      <p className="text-xs text-slate-600">Gildistími</p>
                      <p className="font-bold">{formatExpiration(giftcard.expiration_date)}</p>
                    </div>
                    
                    {/* Service gift card display */}
                    {giftcard.giftcard_type === 'service' ? (
                      <div className="bg-white p-3 rounded col-span-2">
                        <p className="text-xs text-slate-600">Þjónusta</p>
                        <p className="text-2xl font-bold text-green-600">
                          🛠️ {giftcard.service_name}
                        </p>
                        {giftcard.valid_for_size ? (
                          <p className="text-xs text-amber-600 mt-1 font-medium">
                            📏 Gildir aðeins fyrir: {giftcard.valid_for_size_label || giftcard.valid_for_size}
                          </p>
                        ) : (
                          <p className="text-xs text-green-600 mt-1">
                            ✅ Gildir fyrir allar bílastærðir
                          </p>
                        )}
                      </div>
                    ) : (
                      <div className="bg-white p-3 rounded col-span-2">
                        <p className="text-xs text-slate-600">Núverandi inneign</p>
                        <p className={`text-3xl font-bold ${giftcard.balance > 0 ? 'text-purple-600' : 'text-gray-400'}`}>
                          {giftcard.balance?.toLocaleString('is-IS') || 0} kr.
                        </p>
                        {giftcard.amount !== giftcard.balance && (
                          <p className="text-xs text-slate-500 mt-1">
                            Upprunaleg upphæð: {giftcard.amount?.toLocaleString('is-IS')} kr.
                          </p>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Action button */}
                  {giftcard.giftcard_type === 'service' ? (
                    !giftcard.service_redeemed ? (
                      <Button
                        onClick={copyCodeToClipboard}
                        className="w-full bg-green-600 hover:bg-green-700 text-lg py-6"
                        size="lg"
                      >
                        📋 Afrita kóða fyrir innlausn
                      </Button>
                    ) : (
                      <div className="bg-gray-100 border-2 border-gray-400 p-4 rounded text-center">
                        <p className="font-bold text-gray-700">
                          ✅ Þjónustugjafabréf hefur verið innleyst
                        </p>
                      </div>
                    )
                  ) : (
                    giftcard.balance > 0 ? (
                      <Button
                        onClick={copyCodeToClipboard}
                        className="w-full bg-purple-600 hover:bg-purple-700 text-lg py-6"
                        size="lg"
                      >
                        📋 Afrita kóða fyrir greiðslu
                      </Button>
                    ) : (
                      <div className="bg-gray-100 border-2 border-gray-400 p-4 rounded text-center">
                        <p className="font-bold text-gray-700">
                          ⚠️ Gjafabréf er búið að nota
                        </p>
                      </div>
                    )
                  )}

                  {/* Usage History */}
                  {giftcard.usage_history && giftcard.usage_history.length > 0 && (
                    <details className="mt-4 text-sm">
                      <summary className="cursor-pointer font-medium text-purple-900 hover:text-purple-700">
                        📋 Notkunarsaga ({giftcard.usage_history.length})
                      </summary>
                      <div className="mt-2 space-y-1 pl-4 text-slate-600">
                        {giftcard.usage_history.slice(-5).reverse().map((usage, idx) => (
                          <div key={idx}>
                            • {new Date(usage.date).toLocaleString('is-IS')} - 
                            {usage.amount_used.toLocaleString('is-IS')} kr. notað
                            {usage.redeemed_by && ` af ${usage.redeemed_by}`}
                          </div>
                        ))}
                      </div>
                    </details>
                  )}

                  {/* Reload History (if CARD format) */}
                  {giftcard.format === 'CARD' && giftcard.reload_history && giftcard.reload_history.length > 0 && (
                    <details className="mt-4 text-sm">
                      <summary className="cursor-pointer font-medium text-purple-900 hover:text-purple-700">
                        💰 Hleðslusaga ({giftcard.reload_history.length})
                      </summary>
                      <div className="mt-2 space-y-1 pl-4 text-slate-600">
                        {giftcard.reload_history.slice(-5).reverse().map((reload, idx) => (
                          <div key={idx}>
                            • {new Date(reload.date).toLocaleString('is-IS')} - 
                            +{reload.amount_added.toLocaleString('is-IS')} kr.
                            {reload.reloaded_by && ` af ${reload.reloaded_by}`}
                          </div>
                        ))}
                      </div>
                    </details>
                  )}
                </div>
              )}

              {/* Instructions */}
              {!giftcard && !scanning && (
                <div className="bg-purple-50 p-4 rounded-lg text-sm space-y-2">
                  <p className="font-medium text-purple-900">📖 Leiðbeiningar:</p>
                  <ul className="text-purple-700 space-y-1 list-disc list-inside">
                    <li>Sláðu inn gjafabréfskóðann handvirkt</li>
                    <li>Eða smelltu á "Byrja að skanna"</li>
                    <li>Beindu myndavélinni að QR kóðanum</li>
                    <li>Skoðaðu inneign og gildistíma</li>
                    <li>Afritaðu kóðann til að nota í greiðsluferli</li>
                  </ul>
                </div>
              )}
            </TabsContent>

            {/* BOOKING TAB */}
            <TabsContent value="booking" className="space-y-4">
              {/* Code Input for Booking */}
              <div className="space-y-2">
                <Label>Sláðu inn bókunarkóða</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="Bókunar ID..."
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && lookupBooking()}
                    disabled={!!booking}
                  />
                  {!booking ? (
                    <Button 
                      onClick={lookupBooking} 
                      disabled={loading || !code}
                      className="bg-cyan-600 hover:bg-cyan-700"
                    >
                      {loading ? '⌛ Leita...' : '🔍 Leita'}
                    </Button>
                  ) : (
                    <Button 
                      onClick={resetScanner}
                      variant="outline"
                    >
                      ✕ Hreinsa
                    </Button>
                  )}
                </div>
                <p className="text-xs text-slate-500">
                  Skannaðu QR kóðann úr staðfestingarpósti viðskiptavinar
                </p>
              </div>

              {/* Live Camera Scanner for Booking */}
              {!booking && !scanning && (
                <div className="space-y-2">
                  <Button
                    onClick={startScanning}
                    className="bg-green-600 hover:bg-green-700 w-full text-lg py-6"
                    size="lg"
                  >
                    📷 Byrja að skanna QR kóða
                  </Button>
                </div>
              )}

              {/* Camera View for Booking */}
              {scanning && (
                <div className="space-y-2">
                  <div className="relative border-4 border-cyan-500 rounded-lg overflow-hidden bg-black">
                    <Webcam
                      ref={webcamRef}
                      audio={false}
                      screenshotFormat="image/jpeg"
                      videoConstraints={{
                        facingMode: { ideal: 'environment' },
                        width: { ideal: 1920 },
                        height: { ideal: 1080 }
                      }}
                      className="w-full"
                      onUserMediaError={(error) => {
                        console.error('Camera error:', error);
                        toast.error('Villa við að opna myndavél');
                        stopScanning();
                      }}
                    />
                    
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <div className="w-64 h-64 border-4 border-cyan-400 rounded-lg shadow-lg animate-pulse">
                        <div className="absolute top-0 left-0 w-8 h-8 border-t-4 border-l-4 border-cyan-400"></div>
                        <div className="absolute top-0 right-0 w-8 h-8 border-t-4 border-r-4 border-cyan-400"></div>
                        <div className="absolute bottom-0 left-0 w-8 h-8 border-b-4 border-l-4 border-cyan-400"></div>
                        <div className="absolute bottom-0 right-0 w-8 h-8 border-b-4 border-r-4 border-cyan-400"></div>
                      </div>
                    </div>
                    
                    <div className="absolute bottom-4 left-0 right-0 text-center">
                      <p className="text-white font-bold bg-black bg-opacity-70 px-4 py-2 rounded inline-block">
                        🔍 Að skanna bókunar QR kóða...
                      </p>
                    </div>
                  </div>
                  
                  <canvas ref={canvasRef} style={{ display: 'none' }} />
                  
                  <Button
                    onClick={stopScanning}
                    variant="outline"
                    className="w-full"
                  >
                    ✕ Hætta við
                  </Button>
                </div>
              )}

              {/* Booking Display */}
              {booking && (
                <div className={`border-2 rounded-lg p-6 ${
                  bookingStarted || booking.status === 'in_progress' 
                    ? 'border-green-500 bg-green-50' 
                    : booking.status === 'done' || booking.status === 'paid'
                    ? 'border-gray-400 bg-gray-50'
                    : 'border-cyan-500 bg-cyan-50'
                }`}>
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-2xl font-bold text-slate-900">
                        🚗 {booking.plate}
                      </h3>
                      <p className="text-sm text-slate-700">
                        {booking.owner_name || booking.customer_name || 'Viðskiptavinur'}
                      </p>
                    </div>
                    <Badge variant={
                      booking.status === 'in_progress' || bookingStarted ? 'default' :
                      booking.status === 'done' ? 'secondary' :
                      booking.status === 'paid' ? 'secondary' :
                      'outline'
                    } className={
                      booking.status === 'in_progress' || bookingStarted ? 'bg-green-600' :
                      booking.status === 'done' || booking.status === 'paid' ? 'bg-gray-500' :
                      'bg-cyan-600 text-white'
                    }>
                      {booking.status === 'in_progress' || bookingStarted ? '🔄 Í VINNSLU' :
                       booking.status === 'done' ? '✅ LOKIÐ' :
                       booking.status === 'paid' ? '💰 GREITT' :
                       '⏳ BÍÐ'}
                    </Badge>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="bg-white p-3 rounded">
                      <p className="text-xs text-slate-600">📅 Dagsetning</p>
                      <p className="font-bold">{booking.scheduled_date || booking.date || 'Ekki skráð'}</p>
                    </div>
                    <div className="bg-white p-3 rounded">
                      <p className="text-xs text-slate-600">🕐 Tími</p>
                      <p className="font-bold">{booking.scheduled_time || booking.time || 'Ekki skráð'}</p>
                    </div>
                    <div className="bg-white p-3 rounded">
                      <p className="text-xs text-slate-600">📏 Stærð</p>
                      <p className="font-bold">{booking.size || 'Ekki skráð'}</p>
                    </div>
                    <div className="bg-white p-3 rounded">
                      <p className="text-xs text-slate-600">📞 Sími</p>
                      <p className="font-bold">{booking.phone_number || booking.phone || 'Ekki skráð'}</p>
                    </div>
                    <div className="bg-white p-3 rounded col-span-2">
                      <p className="text-xs text-slate-600">🛠️ Þjónustur</p>
                      <p className="font-bold">
                        {booking.items?.map(item => item.name || item.service).join(', ') || 'Engar þjónustur'}
                      </p>
                    </div>
                    <div className="bg-white p-3 rounded col-span-2">
                      <p className="text-xs text-slate-600">💰 Heildarverð</p>
                      <p className="text-2xl font-bold text-cyan-600">
                        {(booking.total || 0).toLocaleString('is-IS')} kr.
                      </p>
                    </div>
                  </div>

                  {/* Action Button */}
                  {booking.status === 'pending' && !bookingStarted ? (
                    <Button
                      onClick={startBookingJob}
                      disabled={redeeming}
                      className="w-full bg-green-600 hover:bg-green-700 text-lg py-6"
                      size="lg"
                    >
                      {redeeming ? '⌛ Vinnsla...' : '▶️ Byrja verk'}
                    </Button>
                  ) : booking.status === 'in_progress' || bookingStarted ? (
                    <div className="bg-green-100 border-2 border-green-500 p-4 rounded text-center">
                      <p className="font-bold text-green-900 text-lg">
                        ✅ Verk er í vinnslu!
                      </p>
                      <p className="text-sm text-green-700 mt-1">
                        Verkið birtist nú í verklista
                      </p>
                    </div>
                  ) : (
                    <div className="bg-gray-100 border-2 border-gray-400 p-4 rounded text-center">
                      <p className="font-bold text-gray-700">
                        {booking.status === 'done' ? '✅ Verki lokið' : '💰 Verk greitt'}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Instructions */}
              {!booking && !scanning && (
                <div className="bg-cyan-50 p-4 rounded-lg text-sm space-y-2">
                  <p className="font-medium text-cyan-900">📖 Leiðbeiningar:</p>
                  <ul className="text-cyan-700 space-y-1 list-disc list-inside">
                    <li>Viðskiptavinur fær QR kóða í staðfestingarpósti</li>
                    <li>Smelltu á "Byrja að skanna"</li>
                    <li>Beindu myndavélinni að QR kóðanum</li>
                    <li>Verkið byrjar sjálfkrafa!</li>
                  </ul>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
      
      {/* Confirmation Dialog for Double-Click */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>⚠️ Staðfesta notkun</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-700">
              Þú varst að nota klipp fyrir skömmu. Viltu virkilega nota <strong>annan klipp</strong> núna?
            </p>
            
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3">
              <p className="text-sm text-yellow-800">
                <strong>💡 Athugið:</strong> Klippikort á að nota <strong>einn klipp á hverja þvott</strong>.
              </p>
            </div>
            
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => setShowConfirmDialog(false)}
              >
                Nei, hætta við
              </Button>
              <Button
                onClick={() => redeemClip(true)}
                className="bg-orange-600 hover:bg-orange-700"
              >
                Já, nota klipp
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default QRScanner;
